import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import NavigationBar from '../layout/NavigationBar';
import Footer from '../layout/footer';
import LoadingSpinner from './LoadingSpinner';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';
import fetchWithJWT from '../api/fetchWithJWT';
import api from '../api/axiosInstance';
import { keywordsOnOneLine } from '../utils/seoKeywords';

const WebStoreSettings = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [businessId, setBusinessId] = useState('');
    const [logoUploadLoading, setLogoUploadLoading] = useState(false);
    
    // Analytics state
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [selectedDays, setSelectedDays] = useState(30);

    const [bannerSlides, setBannerSlides] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [fourColumnSection, setFourColumnSection] = useState([]);
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [productPage, setProductPage] = useState(1);
    const PRODUCTS_PER_PAGE = 12;

    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [crop, setCrop] = useState({
        unit: '%',
        width: 80,
        height: 40,
        x: 10,
        y: 30
    });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [imageRef, setImageRef] = useState();

    const [storeData, setStoreData] = useState({
        business_name: '',
        store_subdomain: '',
        store_status: 'inactive',
        store_settings: {
            logo_url: '',
            primary_color: '#007bff',
            secondary_color: '#6c757d',
            header_color: '#fff',  // Add this
            footer_color: '#fff',
            accent_color: '#030303ff',
            font_family: 'Arial, sans-serif',
            store_description: '',
            contact_email: '',
            contact_phone: '',
            contact_whatsapp: '',
            social_links: {
                facebook: '',
                instagram: '',
                twitter: ''
            },
            seo_settings: {
                meta_title: '',
                meta_description: '',
                meta_keywords: '',
                google_analytics_id: ''
            },
            // GA4 Analytics Configuration
            analytics_config: {
                ga4_property_id: '',
                ga4_measurement_id: '',
                analytics_connected: false,
                analytics_sync_status: 'none',
                service_account_access_granted: false,
                last_data_fetch: null,
                analytics_last_sync: null,
                analytics_error_message: ''
            },
            banner_section: {
                enabled: false,
                size: 'medium',
                slides: []
            },
            category_section: {
                enabled: false,
                title: 'Shop by Categories',
                selected_categories: []
            },
            featured_section: {
                enabled: false,
                title: 'Featured Products',
                selected_products: []
            }
        }
    });

    useEffect(() => {
        const business_id = localStorage.getItem('business_id');
        if (business_id) {
            setBusinessId(business_id);
            fetchStoreInfo(business_id);
            fetchCategories();
            fetchProducts(business_id);
        }
    }, []);

    // Auto-refresh analytics when date filter changes
    useEffect(() => {
        if (storeData.store_settings?.analytics_config?.analytics_connected && analyticsData) {
            fetchAnalyticsData(selectedDays);
        }
    }, [selectedDays]);

    const updateFetchStoreInfo = (data) => {
        // Ensure google_analytics_id has a safe fallback for existing businesses
        const safeStoreData = {
            ...data.result,
            store_settings: {
                ...data.result.store_settings,
                seo_settings: {
                    ...data.result.store_settings.seo_settings,
                    google_analytics_id: data.result.store_settings.seo_settings?.google_analytics_id ?? ''
                },
                // Ensure analytics_config has safe fallbacks
                analytics_config: {
                    ga4_property_id: data.result.analytics_config?.ga4_property_id ?? '',
                    ga4_measurement_id: data.result.analytics_config?.ga4_measurement_id ?? '',
                    analytics_connected: data.result.analytics_config?.analytics_connected ?? false,
                    analytics_sync_status: data.result.analytics_config?.analytics_sync_status ?? 'none',
                    service_account_access_granted: data.result.analytics_config?.service_account_access_granted ?? false,
                    last_data_fetch: data.result.analytics_config?.last_data_fetch ?? null,
                    analytics_last_sync: data.result.analytics_config?.analytics_last_sync ?? null,
                    analytics_error_message: data.result.analytics_config?.analytics_error_message ?? ''
                }
            }
        };
        
        setStoreData(safeStoreData);

        // Set banner slides
        if (data.result.store_settings.banner_section?.slides) {
            setBannerSlides(data.result.store_settings.banner_section.slides);
        }

        // Set selected categories
        if (data.result.store_settings.category_section?.selected_categories) {
            setSelectedCategories(data.result.store_settings.category_section.selected_categories);
        }

        // Set featured products
        if (data.result.store_settings.featured_section?.selected_products) {
            setFeaturedProducts(data.result.store_settings.featured_section.selected_products);
        }
        if (data.result.store_settings.four_column_section?.columns) {
            setFourColumnSection(data.result.store_settings.four_column_section.columns);
        }
    };

    const fetchStoreInfo = async (businessId) => {
        try {
            setLoading(true);
            const response = await fetchWithJWT(`/business/store/info?business_id=${businessId}`);
            const data = await response.json();

            if (data.status === 'success') {
                // Use the updateFetchStoreInfo function to properly set all data
                updateFetchStoreInfo(data);
            } else {
                toast.error('Failed to load store information');
            }
        } catch (error) {
            console.error('Error fetching store info:', error);
            toast.error('Error loading store information');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check if file is PNG format
        if (file.type !== 'image/png') {
            toast.error('Only PNG format is allowed for logo upload');
            event.target.value = ''; // Clear the input
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_type', 'image');

        try {
            setLogoUploadLoading(true);
            const response = await api.post('/file/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success') {
                const fileUrl = response.data.result[0]?.file_url || response.data.result?.files?.[0]?.file_url;
                if (fileUrl) {
                    handleInputChange(null, 'logo_url', fileUrl);
                    toast.success('Logo uploaded successfully');
                } else {
                    toast.error('Failed to get file URL from response');
                }
            } else {
                toast.error('Failed to upload logo');
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error('Error uploading logo: ' + (error.response?.data?.message || error.message));
        } finally {
            setLogoUploadLoading(false);
            // Clear the input
            event.target.value = '';
        }
    };

    const fetchCategories = async () => {
        const business_id = localStorage.getItem('business_id');
        try {
            const response = await fetchWithJWT(`/category/list?front_category=true&business_id=${business_id}`);
            const data = await response.json();
            if (data.status === 'success') {
                setAvailableCategories(data.result);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchProducts = async (id) => {
        try {
            const [greetingRes, giftRes] = await Promise.all([
                fetchWithJWT(`/greeting/list?business_id=${id}`),
                fetchWithJWT(`/gift/list?business_id=${id}`)
            ]);

            const greetingData = await greetingRes.json();
            const giftData = await giftRes.json();

            const combinedProducts = [
                ...(giftData.status === 'success' ? giftData.result.map(item => ({
                    ...item,
                    type: 'gift',
                    category_name: item.category_details?.[0]?.category || ''
                })) : [])
            ];

            setAvailableProducts(combinedProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    // Client-side search + pagination over the seller's own catalog.
    const filteredProducts = availableProducts.filter(product => {
        const term = productSearchTerm.trim().toLowerCase();
        if (!term) return true;
        return (
            product.name?.toLowerCase().includes(term) ||
            product.category_name?.toLowerCase().includes(term)
        );
    });
    const totalProductPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
    const currentProductPage = Math.min(productPage, totalProductPages);
    const paginatedProducts = filteredProducts.slice(
        (currentProductPage - 1) * PRODUCTS_PER_PAGE,
        currentProductPage * PRODUCTS_PER_PAGE
    );

    const handleProductSearchChange = (value) => {
        setProductSearchTerm(value);
        setProductPage(1);
    };

    const handleBannerUpload = async (event, slideIndex = null) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_type', 'image');

        try {
            setLogoUploadLoading(true);
            const response = await api.post('/file/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success') {
                const fileUrl = response.data.result[0]?.file_url || response.data.result?.files?.[0]?.file_url;

                if (slideIndex !== null) {
                    // Update existing slide
                    const updatedSlides = [...bannerSlides];
                    updatedSlides[slideIndex].image_url = fileUrl;
                    setBannerSlides(updatedSlides);
                    handleInputChange('banner_section', 'slides', updatedSlides);
                } else {
                    // Add new slide
                    const newSlide = {
                        id: Date.now(),
                        image_url: fileUrl,
                        title: '',
                        subtitle: '',
                        button_text: '',
                        button_link: '',
                        active: true
                    };
                    const updatedSlides = [...bannerSlides, newSlide];
                    setBannerSlides(updatedSlides);
                    handleInputChange('banner_section', 'slides', updatedSlides);
                }

                toast.success('Banner image uploaded successfully');
            } else {
                toast.error('Failed to upload banner image');
            }
        } catch (error) {
            console.error('Error uploading banner:', error);
            toast.error('Error uploading banner');
        } finally {
            setLogoUploadLoading(false);
            event.target.value = '';
        }
    };

    const handleSlideUpdate = (slideIndex, field, value) => {
        const updatedSlides = [...bannerSlides];
        updatedSlides[slideIndex][field] = value;
        setBannerSlides(updatedSlides);
        handleInputChange('banner_section', 'slides', updatedSlides);
    };

    const removeSlide = (slideIndex) => {
        const updatedSlides = bannerSlides.filter((_, index) => index !== slideIndex);
        setBannerSlides(updatedSlides);
        handleInputChange('banner_section', 'slides', updatedSlides);
    };

    const toggleCategorySelection = (categoryName) => {
        const isCurrentlySelected = selectedCategories.some(selected =>
            typeof selected === 'object' ? selected.category === categoryName : selected === categoryName
        );

        let updatedCategories;
        if (isCurrentlySelected) {
            // Remove category
            updatedCategories = selectedCategories.filter(selected =>
                typeof selected === 'object' ? selected.category !== categoryName : selected !== categoryName
            );
        } else {
            // Add category with image_url field
            updatedCategories = [...selectedCategories, { category: categoryName, image_url: '' }];
        }

        setSelectedCategories(updatedCategories);
        handleInputChange('category_section', 'selected_categories', updatedCategories);
    };


    const toggleProductSelection = (productId) => {
        const updatedProducts = featuredProducts.includes(productId)
            ? featuredProducts.filter(id => id !== productId)
            : [...featuredProducts, productId];

        setFeaturedProducts(updatedProducts);
        handleInputChange('featured_section', 'selected_products', updatedProducts);
    };

    const toggleSection = (section, enabled) => {
        handleInputChange(section, 'enabled', enabled);
    };

    const handleInputChange = (section, field, value) => {
        if (section) {
            setStoreData(prev => ({
                ...prev,
                store_settings: {
                    ...prev.store_settings,
                    [section]: {
                        ...prev.store_settings[section],
                        [field]: value
                    }
                }
            }));
        } else {
            setStoreData(prev => ({
                ...prev,
                store_settings: {
                    ...prev.store_settings,
                    [field]: value
                }
            }));
        }
    };

    const handleSaveSettings = async () => {
        try {
            // ✅ Validation for Google Analytics ID
            const gaId = storeData.store_settings.seo_settings.google_analytics_id;
            if (gaId && gaId.trim() !== '') {
                const gaIdRegex = /^G-[A-Z0-9]{4,}$/;
                if (!gaIdRegex.test(gaId.trim())) {
                    toast.error('Invalid Google Analytics ID format. Expected: G-XXXXXXXXXX');
                    return;
                }
            }

            // ✅ Validation for Four Column Section
            if (storeData.store_settings.four_column_section?.enabled) {
                const columns = storeData.store_settings.four_column_section?.columns || [];

                const invalidColumnIndex = columns.findIndex(col => {
                    // check title
                    const titleMissing = !col.title || col.title.trim() === "";

                    // check at least one image
                    const hasImage = col.items?.some(item => item.image_url?.trim() !== "");

                    // check at least one description
                    const hasDescription = col.items?.some(item => item.description?.trim() !== "");

                    return titleMissing || !hasImage || !hasDescription;
                });

                if (invalidColumnIndex !== -1) {
                    toast.error(
                        `Column ${invalidColumnIndex + 1}: Title, Image, and Description are required`
                    );
                    return; // ⛔ stop saving
                }
            }
            if (storeData.store_settings.category_section?.enabled) {
                const categories = storeData.store_settings.category_section?.selected_categories || [];

                const invalidCategoryIndex = categories.findIndex(cat => {
                    if (typeof cat === 'object') {
                        const imageMissing = !cat.image_url || cat.image_url.trim() === "";
                        const descriptionMissing = !cat.description || cat.description.trim() === "";
                        const priceMissing = !cat.price || cat.price <= 0;
                        const originalPriceMissing = !cat.original_price || cat.original_price <= 0;

                        return imageMissing || descriptionMissing || priceMissing || originalPriceMissing;
                    }
                    return true; // If it's not an object, it's missing required data
                });

                if (invalidCategoryIndex !== -1) {
                    const categoryName = categories[invalidCategoryIndex]?.category || `Category ${invalidCategoryIndex + 1}`;
                    toast.error(
                        `${categoryName}: Image, Description, Current Price (₹), and Original Price (₹) are required fields`
                    );
                    return; // ⛔ stop saving
                }
            }

            setLoading(true);
            const response = await fetchWithJWT('/business/store/settings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    business_id: businessId,
                    ...storeData.store_settings,
                    header_color: storeData.store_settings.header_color,
                    footer_color: storeData.store_settings.footer_color,
                    facebook: storeData.store_settings.social_links.facebook,
                    instagram: storeData.store_settings.social_links.instagram,
                    twitter: storeData.store_settings.social_links.twitter,
                    meta_title: storeData.store_settings.seo_settings.meta_title,
                    meta_description: storeData.store_settings.seo_settings.meta_description,
                    meta_keywords: storeData.store_settings.seo_settings.meta_keywords,
                    google_analytics_id: storeData.store_settings.seo_settings.google_analytics_id?.trim() || '',
                    
                    // GA4 Analytics Configuration
                    ga4_property_id: storeData.store_settings.analytics_config?.ga4_property_id?.trim() || '',
                    ga4_measurement_id: storeData.store_settings.analytics_config?.ga4_measurement_id?.trim() || '',

                    banner_section: storeData.store_settings.banner_section,
                    category_section: storeData.store_settings.category_section,
                    featured_section: storeData.store_settings.featured_section,
                    four_column_section: storeData.store_settings.four_column_section
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                toast.success('Store settings updated successfully');
            } else {
                toast.error('Failed to update store settings');
            }
        } catch (error) {
            console.error('Error updating store settings:', error);
            toast.error('Error updating store settings');
        } finally {
            setLoading(false);
        }
    };


    const handleToggleStore = async (newStatus) => {
        try {
            setLoading(true);
            const response = await fetchWithJWT('/business/store/toggle', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    business_id: businessId,
                    store_status: newStatus
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setStoreData(prev => ({ ...prev, store_status: newStatus }));
                toast.success(`Store ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            } else {
                toast.error('Failed to update store status');
            }
        } catch (error) {
            console.error('Error toggling store status:', error);
            toast.error('Error updating store status');
        } finally {
            setLoading(false);
        }
    };

    // GA4 Analytics Functions
    const handleAnalyticsInputChange = (field, value) => {
        setStoreData(prev => ({
            ...prev,
            store_settings: {
                ...prev.store_settings,
                analytics_config: {
                    ...prev.store_settings.analytics_config,
                    [field]: value
                }
            }
        }));
    };

    const testGA4Connection = async () => {
        const propertyId = storeData.store_settings.analytics_config?.ga4_property_id?.trim();
        
        if (!propertyId) {
            toast.error('Please enter GA4 Property ID first');
            return;
        }

        // Validate Property ID format (should be numeric)
        if (!/^\d+$/.test(propertyId)) {
            toast.error('GA4 Property ID should be numeric (e.g., 123456789)');
            return;
        }

        try {
            setAnalyticsLoading(true);
            const response = await fetchWithJWT('/analytics/test-connection', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    business_id: businessId,
                    ga4_property_id: propertyId
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('GA4 connection successful! Analytics data is now available.');
                // Update the analytics config with connection status
                handleAnalyticsInputChange('analytics_connected', true);
                handleAnalyticsInputChange('analytics_sync_status', 'connected');
                handleAnalyticsInputChange('analytics_error_message', '');
            } else {
                toast.error(`Connection failed: ${data.error}`);
                handleAnalyticsInputChange('analytics_connected', false);
                handleAnalyticsInputChange('analytics_sync_status', 'error');
                handleAnalyticsInputChange('analytics_error_message', data.error);
            }
        } catch (error) {
            console.error('Error testing GA4 connection:', error);
            toast.error('Error testing connection');
            handleAnalyticsInputChange('analytics_connected', false);
            handleAnalyticsInputChange('analytics_sync_status', 'error');
            handleAnalyticsInputChange('analytics_error_message', error.message);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const fetchAnalyticsData = async (days = 30) => {
        if (!storeData.store_settings.analytics_config?.analytics_connected) {
            toast.error('Please test and connect GA4 first');
            return;
        }

        try {
            setAnalyticsLoading(true);
            const response = await fetchWithJWT(`/analytics/comprehensive?days=${days}&business_id=${businessId}`);
            const data = await response.json();

            if (data.success) {
                setAnalyticsData(data.data);
                toast.success('Analytics data loaded successfully');
            } else {
                toast.error(`Failed to load analytics: ${data.error}`);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Error loading analytics data');
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const updateCategoryImage = (categoryName, imageUrl) => {
        const updatedCategories = selectedCategories.map(selected => {
            if (typeof selected === 'object' && selected.category === categoryName) {
                return { ...selected, image_url: imageUrl };
            } else if (selected === categoryName) {
                return { category: categoryName, image_url: imageUrl };
            }
            return selected;
        });

        setSelectedCategories(updatedCategories);
        handleInputChange('category_section', 'selected_categories', updatedCategories);
    };

    const handleCategoryImageUpload = async (event, categoryName) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_type', 'image');

        try {
            setLogoUploadLoading(true);
            const response = await api.post('/file/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success') {
                const fileUrl = response.data.result[0]?.file_url || response.data.result?.files?.[0]?.file_url;
                updateCategoryImage(categoryName, fileUrl);
                toast.success('Category image uploaded successfully');
            } else {
                toast.error('Failed to upload category image');
            }
        } catch (error) {
            console.error('Error uploading category image:', error);
            toast.error('Error uploading category image');
        } finally {
            setLogoUploadLoading(false);
            event.target.value = '';
        }
    };

    const handleColumnImageUpload = async (event, columnIndex, imageIndex) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_type', 'image');

        try {
            setLogoUploadLoading(true);
            const response = await api.post('/file/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success') {
                const fileUrl = response.data.result[0]?.file_url || response.data.result?.files?.[0]?.file_url;
                updateColumnImage(columnIndex, imageIndex, 'image_url', fileUrl);
                toast.success('Column image uploaded successfully');
            } else {
                toast.error('Failed to upload column image');
            }
        } catch (error) {
            console.error('Error uploading column image:', error);
            toast.error('Error uploading column image');
        } finally {
            setLogoUploadLoading(false);
            event.target.value = '';
        }
    };

    const updateColumnImage = (columnIndex, imageIndex, field, value) => {
        const updatedColumns = [...fourColumnSection];
        if (!updatedColumns[columnIndex]) {
            updatedColumns[columnIndex] = { title: '', items: [] };
        }
        if (!updatedColumns[columnIndex].items[imageIndex]) {
            updatedColumns[columnIndex].items[imageIndex] = { image_url: '', description: '', link_url: '' };
        }
        updatedColumns[columnIndex].items[imageIndex][field] = value;
        setFourColumnSection(updatedColumns);
        handleInputChange('four_column_section', 'columns', updatedColumns);
    };

    const updateColumnTitle = (columnIndex, title) => {
        const updatedColumns = [...fourColumnSection];
        if (!updatedColumns[columnIndex]) {
            updatedColumns[columnIndex] = { title: '', items: [] };
        }
        updatedColumns[columnIndex].title = title;
        setFourColumnSection(updatedColumns);
        handleInputChange('four_column_section', 'columns', updatedColumns);
    };

    const addColumn = () => {
        if (fourColumnSection.length < 8) {
            const newColumn = {
                title: '',
                items: [
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' }
                ]
            };
            const updatedColumns = [...fourColumnSection, newColumn];
            setFourColumnSection(updatedColumns);
            handleInputChange('four_column_section', 'columns', updatedColumns);
        }
    };

    const removeColumn = (columnIndex) => {
        const updatedColumns = fourColumnSection.filter((_, index) => index !== columnIndex);
        setFourColumnSection(updatedColumns);
        handleInputChange('four_column_section', 'columns', updatedColumns);
    };
    const updateCategoryData = (categoryName, field, value) => {
        const updatedCategories = selectedCategories.map(selected => {
            if (typeof selected === 'object' && selected.category === categoryName) {
                const updatedCategory = { ...selected, [field]: value };

                // Auto-calculate offer percentage when price or original_price changes
                if ((field === 'price' || field === 'original_price') &&
                    updatedCategory.price && updatedCategory.original_price &&
                    updatedCategory.original_price > updatedCategory.price) {
                    updatedCategory.offer_percentage = Math.round(
                        ((updatedCategory.original_price - updatedCategory.price) / updatedCategory.original_price) * 100
                    );
                } else if (field === 'price' || field === 'original_price') {
                    updatedCategory.offer_percentage = 0;
                }

                return updatedCategory;
            } else if (selected === categoryName) {
                const newCategoryData = {
                    category: categoryName,
                    image_url: field === 'image_url' ? value : '',
                    description: field === 'description' ? value : '',
                    price: field === 'price' ? value : 0,
                    original_price: field === 'original_price' ? value : 0,
                    offer_percentage: field === 'offer_percentage' ? value : 0,
                    offer_text: field === 'offer_text' ? value : ''
                };

                // Auto-calculate offer percentage
                if ((field === 'price' || field === 'original_price') &&
                    newCategoryData.price && newCategoryData.original_price &&
                    newCategoryData.original_price > newCategoryData.price) {
                    newCategoryData.offer_percentage = Math.round(
                        ((newCategoryData.original_price - newCategoryData.price) / newCategoryData.original_price) * 100
                    );
                }

                return newCategoryData;
            }
            return selected;
        });

        setSelectedCategories(updatedCategories);
        handleInputChange('category_section', 'selected_categories', updatedCategories);
    };

    const handleLogoSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check if file is PNG format
        if (file.type !== 'image/png') {
            toast.error('Only PNG format is allowed for logo upload');
            event.target.value = ''; // Clear the input
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setSelectedImage(reader.result);
            setCropModalOpen(true);
        };
        reader.readAsDataURL(file);
    };

    const getCroppedImg = (image, crop) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width * scaleX,
            crop.height * scaleY
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg', 0.9);
        });
    };

    const handleCropAndUpload = async () => {
        if (!completedCrop || !imageRef) return;

        try {
            setLogoUploadLoading(true);

            const croppedImageBlob = await getCroppedImg(imageRef, completedCrop);

            const formData = new FormData();
            formData.append('file', croppedImageBlob, 'cropped-logo.jpg');
            formData.append('upload_type', 'image');

            const response = await api.post('/file/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success') {
                const fileUrl = response.data.result[0]?.file_url || response.data.result?.files?.[0]?.file_url;
                handleInputChange(null, 'logo_url', fileUrl);
                toast.success('Logo uploaded and cropped successfully');
                setCropModalOpen(false);
                setSelectedImage(null);
            } else {
                toast.error('Failed to upload cropped logo');
            }
        } catch (error) {
            console.error('Error uploading cropped logo:', error);
            toast.error('Error uploading cropped logo');
        } finally {
            setLogoUploadLoading(false);
        }
    };



    const storeUrl = `https://${storeData.store_subdomain}.mytamilkart.com`;
    localStorage.setItem("storeUrl", storeUrl);

    if (loading && !storeData.business_name) {
        return (
            <div className="wrapper" id="root">
                <LoadingSpinner message="Loading store settings..." />
            </div>
        );
    }



    const CroppedImagePreview = ({ image, crop }) => {
        const [croppedImageUrl, setCroppedImageUrl] = useState('');

        useEffect(() => {
            if (!crop || !image) return;

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            canvas.width = crop.width;
            canvas.height = crop.height;

            ctx.drawImage(
                image,
                crop.x * scaleX,
                crop.y * scaleY,
                crop.width * scaleX,
                crop.height * scaleY,
                0,
                0,
                crop.width,
                crop.height
            );

            setCroppedImageUrl(canvas.toDataURL('image/jpeg', 0.9));
        }, [crop, image]);

        if (!croppedImageUrl) return null;

        return (
            <img
                src={croppedImageUrl}
                alt="Cropped preview"
                style={{
                    maxWidth: '100%',
                    maxHeight: '80px',
                    objectFit: 'contain',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                }}
            />
        );
    };

    return (
        <div className="wrapper">
            <header>
                <NavigationBar />
            </header>
            <div className="content-wrapper">
                {/* Content Header */}
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">{t('web_store_settings', 'Web Store Settings')}</h1>
                            </div>
                            <div className="col-sm-6">
                                <div className="float-sm-right">
                                    <span className={`badge badge-lg ${storeData.store_status === 'active' ? 'badge-success' :
                                        storeData.store_status === 'inactive' ? 'badge-warning' : 'badge-danger'
                                        }`}>
                                        {storeData.store_status.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">

                        {/* Store Status and Store Appearance side by side (stacked below lg) */}
                        <div className="row">
                            <div className="col-lg-6 d-flex flex-column">
                                <div className="card flex-fill">
                                    <div className="card-header">
                                        <h3 className="card-title">{t('store_status', 'Store Status')}</h3>
                                    </div>
                                    <div className="card-body">
                                        <p><strong>{t('store_url', 'Store URL')}:</strong> <a href={storeUrl} target="_blank" rel="noopener noreferrer">{storeUrl}</a></p>
                                        <p><strong>{t('current_status', 'Current Status')}:</strong>
                                            <span className={`badge ml-2 ${storeData.store_status === 'active' ? 'badge-success' :
                                                storeData.store_status === 'inactive' ? 'badge-warning' : 'badge-danger'
                                                }`}>
                                                {storeData.store_status}
                                            </span>
                                        </p>
                                        <div className="mt-3">
                                            {storeData.store_status === 'active' ? (
                                                <button
                                                    className="btn btn-warning mr-2 mobile-warning-btn"
                                                    onClick={() => handleToggleStore('inactive')}
                                                    disabled={loading}
                                                >
                                                    {t('deactivate_store')}
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn btn-success mr-2"
                                                    onClick={() => handleToggleStore('active')}
                                                    disabled={loading}
                                                >
                                                    {(t('activate_store'))}
                                                </button>
                                            )}
                                            <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="btn btn-info">
                                                <i className="fas fa-external-link-alt"></i> {t('view_store')}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        {/* Store Customization */}
                            <div className="col-lg-6 d-flex flex-column">
                                <div className="card flex-fill">
                                    <div className="card-header">
                                        <h3 className="card-title">{t('store_appearance')}</h3>
                                    </div>
                                    <div className="card-body">
                                        {/* <h4 className='main-title'>Header Section</h4> */}
                                        <div className="row">
                                            <div className="col-12">
                                                

                                                
                                                {/* <div className="form-group">
                                                    <label>{t('header_color')}</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.header_color}
                                                        onChange={(e) => handleInputChange(null, 'header_color', e.target.value)}
                                                    />
                                            </div> */}
                                                <div className="form-group">
                                                    <label>{t('logo_url')}</label>

                                                    

                                                    {/* Logo Preview */}
                                                    {storeData.store_settings.logo_url && (
                                                        <div className="mb-2 position-relative" style={{ maxWidth: '200px' }}>
                                                            <img
                                                                src={storeData.store_settings.logo_url}
                                                                alt="Store Logo"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '80px',
                                                                    objectFit: 'contain',
                                                                    border: '1px solid #dee2e6',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm position-absolute"
                                                                style={{ top: '5px', right: '5px' }}
                                                                onClick={() => handleInputChange(null, 'logo_url', '')}
                                                                title="Remove Logo"
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        </div>
                                                    )}

                                                    <div className="input-group">
                                                        <input
                                                            type="url"
                                                            className="form-control"
                                                            value={storeData.store_settings.logo_url}
                                                            onChange={(e) => handleInputChange(null, 'logo_url', e.target.value)}
                                                            placeholder="https://example.com/logo.png"
                                                        />
                                                        <div className="input-group-append">
                                                            <label className="btn btn-outline-secondary" htmlFor="logoUpload">
                                                                {logoUploadLoading ? (
                                                                    <i className="fas fa-spinner fa-spin"></i>
                                                                ) : (
                                                                    <i className="fas fa-upload"></i>
                                                                )}
                                                                {t('upload_crop')}
                                                            </label>
                                                            <input
                                                                id="logoUpload"
                                                                type="file"
                                                                accept="image/png"
                                                                onChange={handleLogoSelect}
                                                                style={{ display: 'none' }}
                                                                disabled={logoUploadLoading}
                                                            />
                                                        </div>
                                                    </div>
                                                    <small className="text-muted">{t('logo_url_help')} - Only PNG format is allowed for logo uploads.</small>
                                                </div>
                                            </div>

                                            {/* Crop Modal */}
                                            {cropModalOpen && (
                                                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                                    <div className="modal-dialog modal-lg">
                                                        <div className="modal-content">
                                                            <div className="modal-header">
                                                                <h5 className="modal-title">{t('crop_logo_image')}</h5>
                                                                <button
                                                                    type="button"
                                                                    className="close"
                                                                    onClick={() => setCropModalOpen(false)}
                                                                >
                                                                    <span>&times;</span>
                                                                </button>
                                                            </div>
                                                            <div className="modal-body">
                                                                <div className="row">
                                                                    {/* Left side - Cropping interface */}
                                                                    <div className="col-md-8">
                                                                        <div className="mb-3 text-center">
                                                                            <small className="text-muted">
                                                                                Drag to select the logo area (recommended: wide rectangle format)
                                                                            </small>
                                                                        </div>
                                                                        {selectedImage && (
                                                                            <div style={{ maxHeight: '400px', overflow: 'hidden' }}>
                                                                                <ReactCrop
                                                                                    crop={crop}
                                                                                    onChange={(newCrop) => setCrop(newCrop)}
                                                                                    onComplete={(newCrop) => setCompletedCrop(newCrop)}
                                                                                    aspect={4.5 / 1}
                                                                                    minWidth={200}
                                                                                    minHeight={50}
                                                                                    style={{ maxWidth: '100%' }}
                                                                                >
                                                                                    <img
                                                                                        ref={setImageRef}
                                                                                        src={selectedImage}
                                                                                        alt="Crop preview"
                                                                                        style={{
                                                                                            maxWidth: '100%',
                                                                                            maxHeight: '350px',
                                                                                            display: 'block'
                                                                                        }}
                                                                                        onLoad={() => {
                                                                                            // Set initial crop when image loads
                                                                                            if (imageRef) {
                                                                                                const { width, height } = imageRef;
                                                                                                setCrop({
                                                                                                    unit: 'px',
                                                                                                    width: Math.min(width * 0.8, 300),
                                                                                                    height: Math.min(height * 0.4, 100),
                                                                                                    x: width * 0.1,
                                                                                                    y: height * 0.35
                                                                                                });
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </ReactCrop>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Right side - Cropped preview */}
                                                                    <div className="col-md-4">
                                                                        <div className="text-center">
                                                                            <h6>{t('preview')}</h6>
                                                                            <small className="text-muted d-block mb-2">This is how your logo will look</small>

                                                                            <div
                                                                                style={{
                                                                                    border: '2px dashed #dee2e6',
                                                                                    padding: '7px',
                                                                                    borderRadius: '8px',
                                                                                    backgroundColor: '#FDF9F2',
                                                                                    minHeight: '72px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                            >
                                                                                {completedCrop && imageRef ? (
                                                                                    <CroppedImagePreview
                                                                                        image={imageRef}
                                                                                        crop={completedCrop}
                                                                                    />
                                                                                ) : (
                                                                                    <div className="text-muted">
                                                                                        <i className="fas fa-image fa-2x mb-2"></i>
                                                                                        <div>Cropped logo will appear here</div>
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            <div className="mt-3">
                                                                                <small className="text-info">
                                                                                    <i className="fas fa-info-circle" style={{ marginRight: "6px" }}></i>
                                                                                    Logo will be displayed at 40px height in the header
                                                                                </small>
                                                                            </div>

                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="modal-footer">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary"
                                                                    onClick={() => setCropModalOpen(false)}
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    onClick={handleCropAndUpload}
                                                                    disabled={logoUploadLoading || !completedCrop}
                                                                >
                                                                    {logoUploadLoading ? 'Uploading...' : 'Crop & Upload'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {/* <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>{t('font_family')}</label>
                                                    <select
                                                        className="form-control"
                                                        value={storeData.store_settings.font_family}
                                                        onChange={(e) => handleInputChange(null, 'font_family', e.target.value)}
                                                    >
                                                        <option value="Arial, sans-serif">Arial</option>
                                                        <option value="Georgia, serif">Georgia</option>
                                                        <option value="Helvetica, sans-serif">Helvetica</option>
                                                        <option value="Times New Roman, serif">Times New Roman</option>
                                                        <option value="Verdana, sans-serif">Verdana</option>
                                                    </select>
                                                </div>
                                            </div> */}
                                        </div>

                                        

                                        {/* <h4 className='main-title'>Body Section</h4> */}

                                        <div className="row">
                                            
                                            {/* <div className="col-md-4">
                                                <div className="form-group">
                                                    <label>{t('primary_color')}</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.primary_color}
                                                        onChange={(e) => handleInputChange(null, 'primary_color', e.target.value)}
                                                    />
                                                </div>
                                            </div> */}
                                            
                                            {/* <div className="col-md-4">
                                                <div className="form-group">
                                                    <label>{t('secondary_color')}</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.secondary_color}
                                                        onChange={(e) => handleInputChange(null, 'secondary_color', e.target.value)}
                                                    />
                                                </div>
                                            </div> */}
                                            {/* <div className="col-md-4">
                                                <div className="form-group">
                                                    <label>{t('accent_color')}</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.accent_color}
                                                        onChange={(e) => handleInputChange(null, 'accent_color', e.target.value)}
                                                    />
                                                </div>
                                            </div> */}
                                            
                                        </div>
                                        {/* <div className="form-group">
                                            <label>{t('store_description')}</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={storeData.store_settings.store_description}
                                                onChange={(e) => handleInputChange(null, 'store_description', e.target.value)}
                                                placeholder="Describe your store..."
                                            />
                                        </div> */}

                                        {/* <h4 class="main-title">Footer Section</h4> */}


                                        <div className="row">
                                            {/* <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>{t('header_color')}</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.header_color}
                                                        onChange={(e) => handleInputChange(null, 'header_color', e.target.value)}
                                                    />
                                                </div>
                                            </div> */}
                                            {/* <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>{t('footer_color')}</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.footer_color}
                                                        onChange={(e) => handleInputChange(null, 'footer_color', e.target.value)}
                                                    />
                                                </div>
                                            </div> */}
                                        </div>

                                        {/* <div className="form-group">
                                            <label>{t('store_description')}</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={storeData.store_settings.store_description}
                                                onChange={(e) => handleInputChange(null, 'store_description', e.target.value)}
                                                placeholder="Describe your store..."
                                            />
                                        </div> */}
                                    </div>
                                </div>
                            </div>

                            {/* <div className="col-md-4">
                                
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Preview</h3>
                                    </div>
                                    <div className="card-body text-center" style={{
                                        backgroundColor: storeData.store_settings.secondary_color,
                                        fontFamily: storeData.store_settings.font_family
                                    }}>
                                       
                                        <div style={{
                                            backgroundColor: storeData.store_settings.header_color,
                                            padding: '10px',
                                            marginBottom: '10px',
                                            borderRadius: '4px'
                                        }}>
                                            <small style={{ color: '#666' }}>{t('header')}</small>
                                        </div>

                                        {storeData.store_settings.logo_url && (
                                            <img
                                                src={storeData.store_settings.logo_url}
                                                alt="Store Logo"
                                                style={{ maxWidth: '100px', maxHeight: '50px' }}
                                                className="mb-2"
                                            />
                                        )}
                                        <h4 style={{ color: storeData.store_settings.primary_color }}>
                                            {storeData.business_name}
                                        </h4>
                                        <p style={{ color: storeData.store_settings.primary_color }}>
                                            {storeData.store_settings.store_description || "Your store description will appear here"}
                                        </p>
                                        <button
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: storeData.store_settings.accent_color,
                                                color: 'white',
                                                fontFamily: storeData.store_settings.font_family
                                            }}
                                        >
                                            {t('sample_button')}
                                        </button>

                                     
                                        <div style={{
                                            backgroundColor: storeData.store_settings.footer_color,
                                            padding: '10px',
                                            marginTop: '10px',
                                            borderRadius: '4px'
                                        }}>
                                            <small style={{ color: '#666' }}>{t('footer')}</small>
                                        </div>
                                    </div>
                                </div>
                            </div> */}
                        </div>

                        {/* Contact Information */}
                        <div className="row">
                            <div className="col-md-6 d-flex flex-column">
                                <div className="card flex-fill">
                                    <div className="card-header">
                                        <h3 className="card-title">{t('contact_information')}</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label>{t('contact_email')}</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={storeData.store_settings.contact_email}
                                                onChange={(e) => handleInputChange(null, 'contact_email', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('contact_phone')}</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                value={storeData.store_settings.contact_phone}
                                                onChange={(e) => handleInputChange(null, 'contact_phone', e.target.value)}
                                                maxLength={10}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Contact Whatsapp</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                value={storeData.store_settings.contact_whatsapp}
                                                onChange={(e) => handleInputChange(null, 'contact_whatsapp', e.target.value)}
                                                maxLength={10}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6 d-flex flex-column">
                                <div className="card flex-fill">
                                    <div className="card-header">
                                        <h3 className="card-title">{t('social_media_links')}</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label>{t('facebook')}</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={storeData.store_settings.social_links.facebook}
                                                onChange={(e) => handleInputChange('social_links', 'facebook', e.target.value)}
                                                placeholder="https://facebook.com/yourpage"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('instagram')}</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={storeData.store_settings.social_links.instagram}
                                                onChange={(e) => handleInputChange('social_links', 'instagram', e.target.value)}
                                                placeholder="https://instagram.com/yourhandle"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('twitter')}</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={storeData.store_settings.social_links.twitter}
                                                onChange={(e) => handleInputChange('social_links', 'twitter', e.target.value)}
                                                placeholder="https://twitter.com/yourhandle"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* SEO Settings */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">{t('seo_settings')}</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>{t('meta_title')}</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={storeData.store_settings.seo_settings.meta_title}
                                                        onChange={(e) => handleInputChange('seo_settings', 'meta_title', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>{t('meta_keywords')}</label>
                                                    {/* One line: keywords saved one per line (the old textarea) show comma-separated */}
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={keywordsOnOneLine(storeData.store_settings.seo_settings.meta_keywords)}
                                                        onChange={(e) => handleInputChange('seo_settings', 'meta_keywords', e.target.value)}
                                                        placeholder="keyword1, keyword2, keyword3"
                                                    />
                                                </div>
                                                {/* <div className="form-group">
                                                    <label>{t('google_analytics_id', 'Google Analytics ID')}</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={storeData.store_settings.seo_settings.google_analytics_id}
                                                        onChange={(e) => handleInputChange('seo_settings', 'google_analytics_id', e.target.value)}
                                                        placeholder="G-XXXXXXXXXX"
                                                    />
                                                    <small className="text-muted">
                                                        Enter your Google Analytics 4 Measurement ID (starts with G-)
                                                    </small>
                                                </div> */}
                                                
                                                {/* GA4 Property ID Field */}
                                                {/* <div className="form-group">
                                                    <label>GA4 Property ID <span className="text-danger">*</span></label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={storeData.store_settings.analytics_config?.ga4_property_id || ''}
                                                        onChange={(e) => handleAnalyticsInputChange('ga4_property_id', e.target.value)}
                                                        placeholder="123456789"
                                                    />
                                                    <small className="text-muted">
                                                        <strong>Required for Analytics:</strong> Enter your GA4 Property ID (numeric only, e.g., 123456789)
                                                        <br />
                                                        <i className="fas fa-info-circle"></i> Find this in GA4 Admin → Property Settings
                                                    </small>
                                                </div> */}

                                                {/* Connection Status */}
                                                {/* {storeData.store_settings.analytics_config?.ga4_property_id && (
                                                    <div className="form-group">
                                                        <label>Analytics Connection Status</label>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`badge ${
                                                                storeData.store_settings.analytics_config?.analytics_connected 
                                                                    ? 'badge-success' 
                                                                    : 'badge-warning'
                                                            } mr-2`}>
                                                                {storeData.store_settings.analytics_config?.analytics_connected 
                                                                    ? 'Connected' 
                                                                    : 'Not Connected'}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={testGA4Connection}
                                                                disabled={analyticsLoading}
                                                            >
                                                                {analyticsLoading ? (
                                                                    <>
                                                                        <i className="fas fa-spinner fa-spin"></i> Testing...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="fas fa-plug"></i> Test Connection
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                        {storeData.store_settings.analytics_config?.analytics_error_message && (
                                                            <small className="text-danger d-block mt-1">
                                                                <i className="fas fa-exclamation-triangle"></i> {storeData.store_settings.analytics_config.analytics_error_message}
                                                            </small>
                                                        )}
                                                        <small className="text-info d-block mt-1">
                                                            <i className="fas fa-lightbulb"></i> <strong>Setup Instructions:</strong>
                                                            <br />1. Add the service account email as Viewer to your GA4 property
                                                            <br />2. Click "Test Connection" to verify access
                                                        </small>
                                                    </div>
                                                )} */}
                                            </div>
                                        </div>
                                        <div className="form-group mb-0">
                                            <label>{t('meta_description')}</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={storeData.store_settings.seo_settings.meta_description}
                                                onChange={(e) => handleInputChange('seo_settings', 'meta_description', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Analytics Display Section */}
                        {storeData.store_settings.analytics_config?.analytics_connected && (
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h3 className="card-title">
                                                <i className="fas fa-chart-line"></i> Analytics Dashboard
                                            </h3>
                                            <div className="d-flex align-items-center">
                                                <div className="mr-3">
                                                    <label className="mb-0 mr-2 text-sm">Time Period:</label>
                                                    <select 
                                                        className="form-control form-control-sm d-inline-block" 
                                                        style={{width: 'auto'}}
                                                        value={selectedDays}
                                                        onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                                                        disabled={analyticsLoading}
                                                    >
                                                        <option value={1}>Last 1 Day</option>
                                                        <option value={3}>Last 3 Days</option>
                                                        <option value={5}>Last 5 Days</option>
                                                        <option value={7}>Last 7 Days</option>
                                                        <option value={14}>Last 14 Days</option>
                                                        <option value={30}>Last 30 Days</option>
                                                        <option value={60}>Last 60 Days</option>
                                                        <option value={90}>Last 90 Days</option>
                                                    </select>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-info mr-2"
                                                    onClick={() => fetchAnalyticsData(selectedDays)}
                                                    disabled={analyticsLoading}
                                                >
                                                    {analyticsLoading ? (
                                                        <>
                                                            <i className="fas fa-spinner fa-spin"></i> Loading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-sync"></i> Refresh Data
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setShowAnalytics(!showAnalytics)}
                                                >
                                                    {showAnalytics ? (
                                                        <>
                                                            <i className="fas fa-eye-slash"></i> Hide
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-eye"></i> Show
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {showAnalytics && (
                                            <div className="card-body">
                                                {analyticsData ? (
                                                    <div>
                                                        {/* Overview Metrics */}
                                                        <div className="row mb-4">
                                                            <div className="col-md-12">
                                                                <h5><i className="fas fa-tachometer-alt"></i> Overview Metrics (Last {selectedDays} Day{selectedDays !== 1 ? 's' : ''})</h5>
                                                                <div className="row">
                                                                    <div className="col-md-2">
                                                                        <div className="info-box bg-info">
                                                                            <span className="info-box-icon"><i className="fas fa-users"></i></span>
                                                                            <div className="info-box-content">
                                                                                <span className="info-box-text">Users</span>
                                                                                <span className="info-box-number">
                                                                                    {analyticsData.overview?.activeUsers?.formatted || '0'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-2">
                                                                        <div className="info-box bg-success">
                                                                            <span className="info-box-icon"><i className="fas fa-mouse-pointer"></i></span>
                                                                            <div className="info-box-content">
                                                                                <span className="info-box-text">Sessions</span>
                                                                                <span className="info-box-number">
                                                                                    {analyticsData.overview?.sessions?.formatted || '0'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-2">
                                                                        <div className="info-box bg-warning">
                                                                            <span className="info-box-icon"><i className="fas fa-heart"></i></span>
                                                                            <div className="info-box-content">
                                                                                <span className="info-box-text">Engagement</span>
                                                                                <span className="info-box-number">
                                                                                    {analyticsData.overview?.engagementRate?.formatted || '0%'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-3">
                                                                        <div className="info-box bg-primary">
                                                                            <span className="info-box-icon"><i className="fas fa-clock"></i></span>
                                                                            <div className="info-box-content">
                                                                                <span className="info-box-text">Avg. Session</span>
                                                                                <span className="info-box-number">
                                                                                    {analyticsData.overview?.averageSessionDuration?.formatted || '0s'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-3">
                                                                        <div className="info-box bg-secondary">
                                                                            <span className="info-box-icon"><i className="fas fa-eye"></i></span>
                                                                            <div className="info-box-content">
                                                                                <span className="info-box-text">Page Views</span>
                                                                                <span className="info-box-number">
                                                                                    {analyticsData.overview?.screenPageViews?.formatted || '0'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Traffic Sources */}
                                                        <div className="row mb-4">
                                                            <div className="col-md-6">
                                                                <h5><i className="fas fa-share-alt"></i> Top Traffic Sources</h5>
                                                                <div className="table-responsive">
                                                                    <table className="table table-sm">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Source</th>
                                                                                <th>Medium</th>
                                                                                <th>Sessions</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {analyticsData.trafficSources?.slice(0, 5).map((source, index) => (
                                                                                <tr key={index}>
                                                                                    <td>{source.source}</td>
                                                                                    <td>
                                                                                        <span className={`badge badge-${
                                                                                            source.medium === 'organic' ? 'success' :
                                                                                            source.medium === 'cpc' ? 'warning' :
                                                                                            source.medium === 'social' ? 'info' : 'secondary'
                                                                                        }`}>
                                                                                            {source.medium}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>{source.sessions.toLocaleString()}</td>
                                                                                </tr>
                                                                            )) || (
                                                                                <tr>
                                                                                    <td colSpan="3" className="text-center text-muted">No data available</td>
                                                                                </tr>
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>

                                                            {/* Top Pages */}
                                                            <div className="col-md-6">
                                                                <h5><i className="fas fa-file-alt"></i> Top Pages</h5>
                                                                <div className="table-responsive">
                                                                    <table className="table table-sm">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Page</th>
                                                                                <th>Views</th>
                                                                                <th>Users</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {analyticsData.topPages?.slice(0, 5).map((page, index) => (
                                                                                <tr key={index}>
                                                                                    <td>
                                                                                        <small title={page.path}>
                                                                                            {page.path.length > 30 ? 
                                                                                                page.path.substring(0, 30) + '...' : 
                                                                                                page.path
                                                                                            }
                                                                                        </small>
                                                                                    </td>
                                                                                    <td>{page.pageViews.toLocaleString()}</td>
                                                                                    <td>{page.users.toLocaleString()}</td>
                                                                                </tr>
                                                                            )) || (
                                                                                <tr>
                                                                                    <td colSpan="3" className="text-center text-muted">No data available</td>
                                                                                </tr>
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Top Locations */}
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <h5><i className="fas fa-globe"></i> Top Locations</h5>
                                                                <div className="table-responsive">
                                                                    <table className="table table-sm">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Country</th>
                                                                                <th>Region</th>
                                                                                <th>City</th>
                                                                                <th>Users</th>
                                                                                <th>Sessions</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {analyticsData.locations?.slice(0, 10).map((location, index) => (
                                                                                <tr key={index}>
                                                                                    <td>{location.country}</td>
                                                                                    <td>{location.region}</td>
                                                                                    <td>{location.city}</td>
                                                                                    <td>{location.users.toLocaleString()}</td>
                                                                                    <td>{location.sessions.toLocaleString()}</td>
                                                                                </tr>
                                                                            )) || (
                                                                                <tr>
                                                                                    <td colSpan="5" className="text-center text-muted">No data available</td>
                                                                                </tr>
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="mt-3">
                                                            <small className="text-muted">
                                                                <i className="fas fa-info-circle"></i> 
                                                                Data refreshed: {analyticsData.timestamp ? new Date(analyticsData.timestamp).toLocaleString() : 'Never'}
                                                                <br />
                                                                <i className="fas fa-lightbulb"></i> 
                                                                This data will be used for AI-powered SEO recommendations in future updates.
                                                            </small>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                                        <h5>No Analytics Data</h5>
                                                        <p className="text-muted">Click "Refresh Data" to load your analytics information.</p>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => fetchAnalyticsData(selectedDays)}
                                                            disabled={analyticsLoading}
                                                        >
                                                            {analyticsLoading ? (
                                                                <>
                                                                    <i className="fas fa-spinner fa-spin"></i> Loading Analytics...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="fas fa-download"></i> Load Analytics Data
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">{t('banner_slider_section')}</h3>
                                        <div
                                            className="custom-control custom-switch ms-3"
                                            style={{ marginLeft: "auto" }}  // adjust until it matches the others
                                        >
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="bannerSectionSwitch"
                                                checked={storeData.store_settings.banner_section.enabled}
                                                onChange={(e) => toggleSection('banner_section', e.target.checked)}
                                            />
                                           <label className="custom-control-label" htmlFor="bannerSectionSwitch">
                                                {storeData.store_settings.banner_section.enabled ? "Disable" : "Enable"}
                                            </label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.banner_section.enabled && (
                                        <div className="card-body">
                                            <div className="form-group">
                                                <label>{t('banner_slider_size', 'Banner Slider Size')}</label>
                                                <select
                                                    className="form-control"
                                                    value={storeData.store_settings.banner_section.size || 'medium'}
                                                    onChange={(e) => handleInputChange('banner_section', 'size', e.target.value)}
                                                >
                                                    <option value="small">Small</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="large">Large</option>
                                                </select>
                                                <small className="text-muted d-block mt-2">
                                                    <i className="fas fa-info-circle"></i> Recommended image size:{' '}
                                                    <strong>
                                                        {(() => {
                                                            const size = storeData.store_settings.banner_section.size || 'medium';
                                                            switch (size) {
                                                                case 'small': return '1920px × 400px';
                                                                case 'medium': return '1920px × 500px';
                                                                case 'large': return '1920px × 600px';
                                                                default: return '1920px × 500px';
                                                            }
                                                        })()}
                                                    </strong>
                                                    {' '}for the best view in user experience.
                                                </small>
                                            </div>
                                            <div className="mb-3">
                                                <label className="btn btn-success">
                                                    <i className="fas fa-plus"></i> {t('add_new_slide')}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleBannerUpload(e)}
                                                        style={{ display: 'none' }}
                                                    />
                                                </label>
                                            </div>

                                            {bannerSlides.map((slide, index) => (
                                                <div key={slide.id || index} className="card mb-3">
                                                    <div className="card-body">
                                                        <div className="row">
                                                            <div className="col-md-4">
                                                                {slide.image_url && (
                                                                    <img
                                                                        src={slide.image_url}
                                                                        alt={`Slide ${index + 1}`}
                                                                        className="img-fluid mb-2"
                                                                        style={{ maxHeight: '150px' }}
                                                                    />
                                                                )}
                                                                <label className="btn btn-outline-primary btn-sm">
                                                                    {t('change_image')}
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={(e) => handleBannerUpload(e, index)}
                                                                        style={{ display: 'none' }}
                                                                    />
                                                                </label>
                                                            </div>
                                                            <div className="col-md-8">
                                                                {/* <div className="form-group">
                                                                    <label>Title</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={slide.title || ''}
                                                                        onChange={(e) => handleSlideUpdate(index, 'title', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label>Subtitle</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={slide.subtitle || ''}
                                                                        onChange={(e) => handleSlideUpdate(index, 'subtitle', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="row">
                                                                    <div className="col-md-6">
                                                                        <div className="form-group">
                                                                            <label>Button Text</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={slide.button_text || ''}
                                                                                onChange={(e) => handleSlideUpdate(index, 'button_text', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="form-group">
                                                                            <label>Button Link</label>
                                                                            <input
                                                                                type="url"
                                                                                className="form-control"
                                                                                value={slide.button_link || ''}
                                                                                onChange={(e) => handleSlideUpdate(index, 'button_link', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div> */}
                                                                <button
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeSlide(index)}
                                                                >
                                                                    {t('remove_slide')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Category Display Section */}
                        {/* <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">{t('category_display_section')}</h3>
                                        <div
                                            className="custom-control custom-switch ms-3"
                                            style={{ marginLeft: "auto" }}  
                                        >
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="categorySectionSwitch"
                                                checked={storeData.store_settings.category_section.enabled}
                                                onChange={(e) => toggleSection('category_section', e.target.checked)}
                                            />
                                             <label className="custom-control-label" htmlFor="categorySectionSwitch">
                                                {storeData.store_settings.category_section.enabled ? "Disable" : "Enable"}
                                            </label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.category_section.enabled && (
                                        <div className="card-body">
                                            <div className="form-group">
                                                <label>{t('section_title')}</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={storeData.store_settings.category_section.title}
                                                    onChange={(e) => handleInputChange('category_section', 'title', e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>{t('select_categories_display')}</label>
                                                <div className="row">
                                                    {availableCategories.map((category) => {
                                                        const isSelected = selectedCategories.some(selected =>
                                                            typeof selected === 'object' ? selected.category === category.category : selected === category.category
                                                        );
                                                        const selectedCategoryData = selectedCategories.find(selected =>
                                                            typeof selected === 'object' ? selected.category === category.category : selected === category.category
                                                        );

                                                        return (
                                                            <div key={category.category} className="col-md-6 mb-4">
                                                                <div className="card" style={{ border: isSelected ? '2px solid #1E5B3A' : '1px solid #dee2e6' }}>
                                                                    <div className="card-body">
                                                                        <div className="custom-control custom-checkbox mb-3">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="custom-control-input"
                                                                                id={`category-${category.category}`}
                                                                                checked={isSelected}
                                                                                onChange={() => toggleCategorySelection(category.category)}
                                                                            />
                                                                            <label
                                                                                className="custom-control-label fw-bold"
                                                                                htmlFor={`category-${category.category}`}
                                                                            >
                                                                                {category.category}
                                                                            </label>
                                                                        </div>

                                                                        {isSelected && (
                                                                            <div className="category-config-section">
                                                                               
                                                                                <div className="mb-3">
                                                                                    <label className="form-label">
                                                                                        {t('category_image')} <span className="text-danger">*</span>
                                                                                    </label>
                                                                                    {selectedCategoryData?.image_url && (
                                                                                        <div className="mb-2">
                                                                                            <img
                                                                                                src={typeof selectedCategoryData === 'object' ? selectedCategoryData.image_url : ''}
                                                                                                alt={category.category}
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    height: '120px',
                                                                                                    objectFit: 'cover',
                                                                                                    borderRadius: '8px',
                                                                                                    border: '1px solid #dee2e6'
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="input-group mb-2">
                                                                                        <input
                                                                                            type="url"
                                                                                            className={`form-control form-control-sm ${!selectedCategoryData?.image_url ? 'is-invalid' : ''
                                                                                                }`}
                                                                                            placeholder="Enter image URL (Required)"
                                                                                            value={typeof selectedCategoryData === 'object' ? selectedCategoryData.image_url || '' : ''}
                                                                                            onChange={(e) => updateCategoryData(category.category, 'image_url', e.target.value)}
                                                                                            required
                                                                                        />
                                                                                    </div>
                                                                                    <label className="btn btn-outline-primary btn-sm w-100">
                                                                                        {logoUploadLoading ? (
                                                                                            <>
                                                                                                <i className="fas fa-spinner fa-spin"></i> {t('uploading')}
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <i className="fas fa-upload"></i> {t('upload_image')}
                                                                                            </>
                                                                                        )}
                                                                                        <input
                                                                                            type="file"
                                                                                            accept="image/*"
                                                                                            onChange={(e) => handleCategoryImageUpload(e, category.category)}
                                                                                            style={{ display: 'none' }}
                                                                                            disabled={logoUploadLoading}
                                                                                        />
                                                                                    </label>
                                                                                </div>

                                                                                
                                                                                <div className="form-group">
                                                                                    <label>
                                                                                        {t('description')} <span className="text-danger">*</span>
                                                                                    </label>
                                                                                    <textarea
                                                                                        className={`form-control form-control-sm ${!selectedCategoryData?.description?.trim() ? 'is-invalid' : ''
                                                                                            }`}
                                                                                        rows="2"
                                                                                        placeholder="Enter category description (Required)"
                                                                                        value={typeof selectedCategoryData === 'object' ? selectedCategoryData.description || '' : ''}
                                                                                        onChange={(e) => updateCategoryData(category.category, 'description', e.target.value)}
                                                                                        required
                                                                                    />
                                                                                </div>

                                                                               
                                                                                <div className="row">
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>
                                                                                                {t('current_price')}  <span className="text-danger">*</span>
                                                                                            </label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className={`form-control form-control-sm ${!selectedCategoryData?.price || selectedCategoryData.price <= 0 ? 'is-invalid' : ''
                                                                                                    }`}
                                                                                                placeholder="0"
                                                                                                min="1"
                                                                                                value={typeof selectedCategoryData === 'object' ? selectedCategoryData.price || '' : ''}
                                                                                                onChange={(e) => updateCategoryData(category.category, 'price', parseInt(e.target.value) || 0)}
                                                                                                required
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>
                                                                                                {t('original_price')} <span className="text-danger">*</span>
                                                                                            </label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className={`form-control form-control-sm ${!selectedCategoryData?.original_price || selectedCategoryData.original_price <= 0 ? 'is-invalid' : ''
                                                                                                    }`}
                                                                                                placeholder="0"
                                                                                                min="1"
                                                                                                value={typeof selectedCategoryData === 'object' ? selectedCategoryData.original_price || '' : ''}
                                                                                                onChange={(e) => updateCategoryData(category.category, 'original_price', parseInt(e.target.value) || 0)}
                                                                                                required
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                               
                                                                                <div className="row">
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>{t('offer_percentage')}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="0"
                                                                                                value={
                                                                                                    typeof selectedCategoryData === 'object' &&
                                                                                                        selectedCategoryData.price &&
                                                                                                        selectedCategoryData.original_price &&
                                                                                                        selectedCategoryData.original_price > selectedCategoryData.price
                                                                                                        ? Math.round(((selectedCategoryData.original_price - selectedCategoryData.price) / selectedCategoryData.original_price) * 100)
                                                                                                        : selectedCategoryData?.offer_percentage || ''
                                                                                                }
                                                                                                onChange={(e) => updateCategoryData(category.category, 'offer_percentage', parseInt(e.target.value) || 0)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>{t('offer_text')}</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control form-control-sm mobile-margin-top"
                                                                                                placeholder="e.g., LIMITED TIME"
                                                                                                value={typeof selectedCategoryData === 'object' ? selectedCategoryData.offer_text || '' : ''}
                                                                                                onChange={(e) => updateCategoryData(category.category, 'offer_text', e.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>

                                                                                </div>


                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div> */}

                        {/* Featured Products Section */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">{t('featured_products_section')}</h3>
                                        <div
                                            className="custom-control custom-switch ms-3"
                                            style={{ marginLeft: "auto" }}   // adjust until it matches the others
                                        >
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="featuredSectionSwitch"
                                                checked={storeData.store_settings.featured_section.enabled}
                                                onChange={(e) => toggleSection('featured_section', e.target.checked)}
                                            />
                                           <label className="custom-control-label" htmlFor="featuredSectionSwitch">
                                                {storeData.store_settings.featured_section.enabled ? "Disable" : "Enable"}
                                            </label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.featured_section.enabled && (
                                        <div className="card-body">
                                            <div className="form-group">
                                                <label>{t('section_title')}</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={storeData.store_settings.featured_section.title}
                                                    onChange={(e) => handleInputChange('featured_section', 'title', e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>{t('select_products_feature')}</label>
                                                <div className="alert alert-info" role="alert">
                                                    <i className="fas fa-info-circle mr-1"></i>
                                                    Adding a product to your store does not automatically show it on the TamilKart main page.
                                                    Select it here so a platform admin can review and feature it on the homepage.
                                                </div>
                                                {availableProducts.length === 0 ? (
                                                    <div className="alert alert-warning text-center" role="alert">
                                                        <i className="fas fa-exclamation-triangle mb-2"></i>
                                                        <h5>{t('no_products_found')}</h5>
                                                        <p className="mb-0">{t('upload_products_first')}</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <input
                                                            type="text"
                                                            className="form-control mb-2"
                                                            placeholder="Search your products by name or category..."
                                                            value={productSearchTerm}
                                                            onChange={(e) => handleProductSearchChange(e.target.value)}
                                                        />

                                                        {filteredProducts.length === 0 ? (
                                                            <div className="alert alert-secondary text-center" role="alert">
                                                                No products match "{productSearchTerm}".
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="row" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                                    {paginatedProducts.map((product) => (
                                                                        <div key={product._id} className="col-md-3 mb-3">
                                                                            <div className="card">
                                                                                <img
                                                                                    src={product.url || product.url}
                                                                                    className="card-img-top"
                                                                                    style={{ height: '120px', objectFit: 'cover' }}
                                                                                    alt={product.name}
                                                                                />
                                                                                <div className="card-body p-2">
                                                                                    <h6 className="card-title" style={{ fontSize: '12px' }}>
                                                                                        {product.name}
                                                                                    </h6>
                                                                                </div>
                                                                                <div className="custom-control custom-checkbox mx-2">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="custom-control-input"
                                                                                        id={`product-${product._id}`}
                                                                                        checked={featuredProducts.includes(product._id)}
                                                                                        onChange={() => toggleProductSelection(product._id)}
                                                                                        disabled={!featuredProducts.includes(product._id) && featuredProducts.length >= 8}
                                                                                    />
                                                                                    <label
                                                                                        className="custom-control-label"
                                                                                        htmlFor={`product-${product._id}`}
                                                                                    >
                                                                                        {t('feature')}
                                                                                    </label>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                {totalProductPages > 1 && (
                                                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-secondary btn-sm"
                                                                            onClick={() => setProductPage(p => Math.max(1, p - 1))}
                                                                            disabled={currentProductPage <= 1}
                                                                        >
                                                                            <i className="fas fa-chevron-left"></i> Prev
                                                                        </button>
                                                                        <small className="text-muted">
                                                                            Page {currentProductPage} of {totalProductPages} ({filteredProducts.length} products)
                                                                        </small>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-secondary btn-sm"
                                                                            onClick={() => setProductPage(p => Math.min(totalProductPages, p + 1))}
                                                                            disabled={currentProductPage >= totalProductPages}
                                                                        >
                                                                            Next <i className="fas fa-chevron-right"></i>
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                        <small className="text-muted d-block mt-2">
                                                            {t('selected_products')}: {featuredProducts.length}/8 {t('products')}
                                                        </small>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* COMMENTED OUT - four_column_display_section */}
                        {/* 
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">{t('four_column_display_section')}</h3>
                                        <div
                                            className="custom-control custom-switch ms-3"
                                            style={{ marginLeft: "auto" }}   // adjust until it matches the others
                                        >
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="fourColumnSectionSwitch"
                                                checked={storeData.store_settings.four_column_section?.enabled || false}
                                                onChange={(e) => toggleSection('four_column_section', e.target.checked)}
                                            />
                                            <label className="custom-control-label" htmlFor="fourColumnSectionSwitch">
                                                 {storeData.store_settings.four_column_section?.enabled ? "Disable" : "Enable"}
                                            </label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.four_column_section?.enabled && (
                                        <div className="card-body">
                                            <div className="form-group">
                                                <label>{t('section_title')}</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={storeData.store_settings.four_column_section?.title || ''}
                                                    onChange={(e) => handleInputChange('four_column_section', 'title', e.target.value)}
                                                    placeholder="e.g., Shop by Category"
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <button
                                                    className="btn btn-success"
                                                    onClick={addColumn}
                                                    disabled={fourColumnSection.length >= 8}
                                                >
                                                    <i className="fas fa-plus"></i> {t('add_column')}
                                                </button>
                                            </div>

                                            <div className="row">
                                                {fourColumnSection.map((column, columnIndex) => (
                                                    <div key={columnIndex} className="col-md-3 mb-4">
                                                        <div className="card border-primary">
                                                            <div className="card-header d-flex justify-content-between align-items-center">
                                                                <h6 className="mb-0">Column {columnIndex + 1}</h6>
                                                                <button
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeColumn(columnIndex)}
                                                                >
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </div>
                                                            <div className="card-body">
                                                                <div className="form-group">
                                                                    <label>{t('column_title')}</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={column.title || ''}
                                                                        onChange={(e) => updateColumnTitle(columnIndex, e.target.value)}
                                                                        placeholder="e.g., TVs & Electronics"
                                                                    />
                                                                </div>

                                                                {[0, 1, 2, 3].map((imageIndex) => (
                                                                    <div key={imageIndex} className="mb-3 p-2 border rounded">
                                                                        <label className="form-label">Item {imageIndex + 1}</label>

                                                                        {column.items?.[imageIndex]?.image_url && (
                                                                            <div className="mb-2">
                                                                                <img
                                                                                    src={column.items[imageIndex].image_url}
                                                                                    alt={`Column ${columnIndex + 1} Item ${imageIndex + 1}`}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '80px',
                                                                                        objectFit: 'cover',
                                                                                        borderRadius: '4px'
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}

                                                                        <div className="form-group">
                                                                            <input
                                                                                type="url"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Image URL"
                                                                                value={column.items?.[imageIndex]?.image_url || ''}
                                                                                onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'image_url', e.target.value)}
                                                                            />
                                                                        </div>

                                                                        <label className="btn btn-outline-primary btn-sm w-100 mb-2">
                                                                            <i className="fas fa-upload"></i> {t('upload')}
                                                                            <input
                                                                                type="file"
                                                                                accept="image/*"
                                                                                onChange={(e) => handleColumnImageUpload(e, columnIndex, imageIndex)}
                                                                                style={{ display: 'none' }}
                                                                                disabled={logoUploadLoading}
                                                                            />
                                                                        </label>

                                                                        <div className="form-group">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Description"
                                                                                value={column.items?.[imageIndex]?.description || ''}
                                                                                onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'description', e.target.value)}
                                                                            />
                                                                        </div>

                                                                        <div className="form-group">
                                                                            <input
                                                                                type="url"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Link URL (optional)"
                                                                                value={column.items?.[imageIndex]?.link_url || ''}
                                                                                onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'link_url', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        */}

                        {/* Save Button */}
                        <div className="row">
                            <div className="col-md-12">
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={handleSaveSettings}
                                    disabled={loading}
                                >
                                    {loading ? `${t('saving')}` : `${t('save_settings')}`}
                                </button>
                            </div>
                        </div>

                    </div>
                </section>
            </div>
            <Footer />



            <style jsx>{`
  @media (max-width: 768px) {
    .form-control-sm.mobile-margin-top {
      margin-top: 20px;
    }
  }

   @media (max-width: 768px) {
    .mobile-warning-btn {
      margin-bottom: 7px !important;
    }
  }
   .main-title {
  font-weight: bold;
  color: #000;      /* optional */
  font-size: 20px;  /* optional */
}


`}</style>



        </div>




    );
};



export default WebStoreSettings;